import type {TStore} from "modules/types/store";
import CONSTANTS from "modules/utils/constants";
import {createSelector} from "reselect";

export const getUserStore = (store: TStore) => store.user;


export const getCurrentUserId = (store: TStore) => store.user.currentUser?.id || 0;

export const sessionState = createSelector([getUserStore], (state) => state.sessionState);

export const getSsoData = createSelector([getUserStore], (state) => state.ssoData);

export const getUserUID = createSelector([getUserStore, getSsoData], (state, sso) => {
	return state.currentUser?.uid || sso?.uid;
});

export const getSentryUser = createSelector([getUserStore], (state) => {
	if (state.currentUser) {
		return {
			id: state.currentUser.id,
			platform: state.currentUser.platform,
			uid: state.currentUser.uid,
		};
	}
	return null;
});

export const loadingUserState = createSelector([getUserStore], (state) => state.loadingState);

export const loadingTermsState = createSelector(
	[getUserStore],
	(state) => state.checkTermsRequestState
);

export const geoRequestStateSelector = createSelector(
	[getUserStore],
	(state) => state.geoRequestState
);

export const isLoggedIn = createSelector([getUserStore], (state) => !!state.currentUser);

export const getIsTermsAccepted = createSelector([getUserStore], (state) => state.terms);

export const getIsOntarioBetting = createSelector(
	[getUserStore],
	(state) => state.countryCode === "CA" && state.stateCode === "ON"
);

export const getIsShowOntarioTerms = createSelector(
	[getUserStore, getIsOntarioBetting],
	(state, isOntario) => {
		if (!isOntario) {
			return false;
		}
		if (!state.currentUser && !state.terms) {
			return true;
		}
		return state.currentUser?.ontarioTerms === false;
	}
);

export const getIsShowTermsModal = createSelector(
	[getUserStore, getIsOntarioBetting],
	(state, isOntario) => {
		if (isOntario) {
			return false;
		}

		return !state.terms;
	}
);

export const getIsSecretEnable = createSelector(
	[getUserStore],
	({isSecretEnabled}) => isSecretEnabled
);
export const getGodModeEnable = createSelector([getUserStore], ({godMode}) => godMode);

export const isCountryAllowedSelector = createSelector(
	[getUserStore],
	(state) => state.isCountryAllowed
);

export const getFavoritePlayersSelector = createSelector(
	[getUserStore],
	(state) => state.favoritePlayers
);

export const getFavoriteSquadsSelector = createSelector(
	[getUserStore],
	(state) => state.favoriteSquads
);

export const getFavoriteRequestState = createSelector(
	[getUserStore],
	(state) => state.favoriteRequestState
);

export const isTutorialViewed = createSelector(
	[getUserStore],
	(state) => state.currentUser?.isTutorialViewed
);

export const isRewardViewed = createSelector(
	[getUserStore],
	(state) => state.currentUser?.isRewardModalViewed
);

export const isSecondTutorialViewed = createSelector(
	[getUserStore],
	(state) => state.currentUser?.isSecondTutorialViewed
);

export const isTutorialOpenForced = createSelector(
	[getUserStore],
	(state) => state.currentUser?.isTutorialOpenForced
);

export const isTooltipViewed = createSelector(
	[getUserStore],
	(state) => state.currentUser?.isTooltipViewed
);

export const isGameMessageViewed = createSelector(
	[getUserStore],
	(state) => state.currentUser?.isGameMessageViewed
);

export const isInsightsTooltipViewed = createSelector(
	[getUserStore],
	(state) => state.currentUser?.isBubbleViewed
);

export const getIsBannerViewed = createSelector(
	[getUserStore],
	(state) => state.currentUser?.isBannerViewed
);

export const isWeeklyPrizeViewed = createSelector(
	[getUserStore],
	(state) => {
		if(CONSTANTS.PRIZE_WEEKLY_DISABLE) {
			return true;
		}
		return state.currentUser?.isPrizeWeeklyModalViewed
	}
);

export const getUserAvatar = createSelector([getUserStore], (state) => state.currentUser?.avatar);

export const isBettingCountySelector = createSelector([getUserStore], (store) => {
	if (!store.countryCode) {
		return false;
	}

	const isBettingByCountry = Boolean(
		CONSTANTS.BETTING_COUNTRIES.find((code) => {
			return code.toUpperCase() === store.countryCode?.toUpperCase();
		})
	);

	const isBettingByState = CONSTANTS.BETTING_STATES.some((code) => {
		return code.toUpperCase() === store.stateCode?.toUpperCase();
	});

	return isBettingByCountry || isBettingByState;
});

export const getUserRewards = createSelector([getUserStore], (state) => state.rewards || []);
